import { useEffect, useState } from "react";
import moment from "moment";
import {
  RootState,
  setFeedbackDateRange,
  setFeedbackSelectedMetros,
} from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { useFetchDocuments } from "../../hooks/useFetchDocuments";
import { MetroFilterWrapper } from "../../components/MetroFilterWrapper";
import DateRangePicker from "./DateRangePicker";
import { feedbackService } from "../../api/feedback";
import { Spin } from "antd";
import DonutChart from "../summary-data/graphs/DonutChart";
import { Loading } from "../../components";
import { TrendChart } from "../summary-data";
import { APICallTrend } from "./graphs/ApiTrend";

interface DailyPageStats {
  totalPages: number;
  totalPlaceholders: number;
  totalDocuments: number;
  averagePages: number;
  averagePlaceholders: number;
}

const FeedbackDataAndGraphs = () => {
  const dispatch = useDispatch();
  const [feedbackStats, setFeedbackStats] = useState<any>([]);
  const [chartData, setChartData] = useState({
    EMPTY_PAGES: 0,
    INVALID_PLACEHOLDER: 0,
    LOW_CONF: 0,
    NO_MATCHING_META_PAGES: 0,
    PH_DOCUMENT_NAME_INCORRECT: 0,
    PLACEHOLDER_MAPPING_INCORRECT: 0,
    SPLIT_INCORRECT: 0,
    SUBMIT_INCORRECT: 0,
    UNCLASSIFIED: 0,
  });
  const [isLoadingChartStats, setIsLoadingChartStats] = useState(false);
  const [isLoadingApiStats, setIsLoadingApiStats] = useState(false);
  const [pageCount, setPageCount] = useState<any>(null);
  const [metrosLoaded, setMetrosLoaded] = useState(false);
  const [hasChartData, setHasChartData] = useState(false); 

  const {
    fromDate: selectedFromDate,
    toDate: selectedToDate,
    selectedMetros,
    range,
  } = useSelector((state: RootState) => ({
    fromDate: state.feedback.selectedDateRange.fromDate,
    toDate: state.feedback.selectedDateRange.toDate,
    selectedMetros: state.feedback.selectedMetros,
    range: state.feedback.selectedDateRange.range,
  }));

  const handleDateRangeChange = (
    dates: [any, any] | null,
    dateStrings: [string, string],
    range: "day" | "week" | "month" | "total"
  ) => {
    const newFromDate =
      dates && range !== "total" ? dateStrings[0] ?? null : null;
    const newToDate =
      dates && range !== "total" ? dateStrings[1] ?? null : null;

    if (
      selectedFromDate === newFromDate &&
      selectedToDate === newToDate &&
      range === range
    ) {
      return;
    }

    dispatch(
      setFeedbackDateRange({
        fromDate: newFromDate,
        toDate: newToDate,
        range: range,
      })
    );
  };

  const onMetroFilterChange = (metros: string[]) => {
    dispatch(setFeedbackSelectedMetros(metros));
  };

  const processFeedbackAnalyticsData = (data: any) => {
    const initialChartData: any = {
      EMPTY_PAGES: 0,
      INVALID_PLACEHOLDER: 0,
      LOW_CONF: 0,
      NO_MATCHING_META_PAGES: 0,
      PH_DOCUMENT_NAME_INCORRECT: 0,
      PLACEHOLDER_MAPPING_INCORRECT: 0,
      SPLIT_INCORRECT: 0,
      SUBMIT_INCORRECT: 0,
      UNCLASSIFIED: 0,
    };
    if (!data || !data.data) {
      return initialChartData;
    }
    const incomingData = data.data;

    let aggregated = { ...initialChartData };

    if (data.data.range === "total") {
      const totalObj = incomingData.total;
      for (const key in initialChartData) {
        if (totalObj.hasOwnProperty(key)) {
          aggregated[key] += totalObj[key];
        }
      }
    } else {
      for (const dateKey in incomingData) {
        const dateData = incomingData[dateKey];
        for (const key in initialChartData) {
          if (dateData.hasOwnProperty(key)) {
            aggregated[key] += dateData[key];
          }
        }
      }
    }

    return aggregated;
  };

  const processFeedbackStats = (data: any) => {
    if (!data || !data.data) {
      return [];
    }
    const EXCLUDED_FEEDBACK_TYPES = ['EMPTY_PAGES', 'INVALID_PLACEHOLDER', 'NO_MATCHING_META_PAGES'];
    const calculateFilteredTotal = (dateData: any) => {
      let filteredTotal = 0;

      for (const key in dateData) {
        if (key !== "total" && !EXCLUDED_FEEDBACK_TYPES.includes(key)) {
          filteredTotal += dateData[key] || 0;
        }
      }
      return Math.max(0, filteredTotal);
    };
    const incomingData = data.data;
    const range = data.range;

    const statsArray: { date: string; feedbacks: number }[] = [];
    if (range === "total") {
      const totalObj = incomingData.total;
      statsArray.push({
        date: "total",
        feedbacks: totalObj.total || 0,
      });
    } else {
      for (const dateKey in incomingData) {
        const dateData = incomingData[dateKey];
        statsArray.push({
          date: dateKey,
          feedbacks: calculateFilteredTotal(dateData),
        });
      }
    }

    return statsArray;
  };

  const processTrendStats = (
    apiData: any
  ): { date: string; feedbackCount: { name: string; count: number }[] }[] => {
    const statsArray: {
      date: string;
      feedbackCount: { name: string; count: number }[];
    }[] = [];
    if (!apiData?.data) return [];

    const dataEntries = Object.entries(apiData.data);

    dataEntries.forEach(([date, feedbackObj]) => {
      if (date === "total") return;

      const feedbackCount = Object.entries(feedbackObj as any)
        .filter(([name]) => name !== "total")
        .map(([name, count]) => ({
          name,
          count: Number(count),
        }));

      statsArray.push({
        date,
        feedbackCount,
      });
    });

    return statsArray;
  };

  const fetchChartStats = async () => {
    setIsLoadingChartStats(true);
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let params: Record<string, any> = {
        timezone,
      };

      if (selectedMetros && selectedMetros.length > 0) {
        params.metros = selectedMetros;
      }

      if (selectedFromDate && selectedToDate && range !== "total") {
        params.fromDate = moment(selectedFromDate).format(
          "YYYY-MM-DD HH:mm:ss"
        );
        params.toDate = moment(selectedToDate).format("YYYY-MM-DD HH:mm:ss");
      }
      params.range = range;

      const { data } = await feedbackService.getFeedbackAnalytics(params);
      const feedbackStatsArray = processFeedbackStats(data);
      setFeedbackStats(feedbackStatsArray);

      const aggregatedChartData = processFeedbackAnalyticsData(data);
      setChartData(aggregatedChartData);
      const feedbackTrend = processTrendStats(data);
      setPageCount(feedbackTrend);
      setHasChartData(true);
    } catch (error) {
      console.error("Error fetching chart stats:", error);
      setHasChartData(false);
    }
    setIsLoadingChartStats(false);
  };

  const { metros, getAllMetros } = useFetchDocuments();

  useEffect(() => {
    const loadMetros = async () => {
      if (metros.length === 0) {
        await getAllMetros();
      }
      setMetrosLoaded(true);
    };
    loadMetros();
  }, []);

  useEffect(() => {
    if (metrosLoaded && range) {
      if (range === "total" || selectedFromDate) {
        fetchChartStats();
      }
    }
  }, [selectedFromDate, selectedToDate, selectedMetros, range, metrosLoaded]);

  const renderDonutChart = () => (
    <div className={`summary-container other-copilots`}>
      <Spin spinning={isLoadingChartStats}>
        <div className="donut-container">
          <h3 className="summary-title">Feedback Received</h3>
          <p>Total feedback processed, grouped by buckets</p>
          <DonutChart
            donutData={[
              // { type: "EMPTY_PAGES", value: chartData.EMPTY_PAGES || 0 },
              // {
              //   type: "INVALID_PLACEHOLDER",
              //   value: chartData.INVALID_PLACEHOLDER || 0,
              // },
              // { type: "LOW_CONF", value: chartData.LOW_CONF || 0 },
              // {
              //   type: "NO_MATCHING_META_PAGES",
              //   value: chartData.NO_MATCHING_META_PAGES || 0,
              // },
              {
                type: "RENAMING",
                value: chartData.PH_DOCUMENT_NAME_INCORRECT || 0,
              },
              {
                type: "MAPPING",
                value: chartData.PLACEHOLDER_MAPPING_INCORRECT || 0,
              },
              {
                type: "SPLIT",
                value: chartData.SPLIT_INCORRECT || 0,
              },
              {
                type: "SUBMIT",
                value: chartData.SUBMIT_INCORRECT || 0,
              },
              // { type: "UNCLASSIFIED", value: chartData.UNCLASSIFIED || 0 },
            ]}
          />
        </div>
      </Spin>
    </div>
  );

  const rangeOptions = [
    { label: "Day", value: "Day" },
    { label: "Week", value: "Week" },
    { label: "Month", value: "Month" },
    { label: "Total", value: "Total" },
    // { label: "Year", value: "Year" },
  ];

  const renderLoading = (message: string) => (
    <div style={{ height: "200px", width: "50%" }}>
      <Loading tip={message} />
    </div>
  );

  const renderProcessingTrend = () => (
    <div className={`summary-container other-copilots`}>
      <div className="processing-trend">
        <TrendChart
          fromDate={moment(selectedFromDate)}
          toDate={moment(selectedToDate)}
          data={feedbackStats}
          title="Feedback Trend"
          description={`Total number of feedbacks`}
          dataKey="feedbacks"
          labelText="No. of Feedbacks"
          range={range}
          defaultGranularity="days"
          includeYears={false}
        />
      </div>
    </div>
  );

  const renderApiCallTrend = () => (
    <div  style={{width:'100%'}}>
      <Spin spinning={isLoadingApiStats}>
        <APICallTrend data={{ tokenData: pageCount }} range={range} />
      </Spin>
    </div>
  );

  return (
    <div className="wrapper">
      <div className="service-status">
        <div className="service-horizontal"></div>
        <DateRangePicker
          rangeOptions={rangeOptions.filter(
            (option) => option.value.toLowerCase() !== range?.toLowerCase()
          )}
          onDateRangeChange={handleDateRangeChange}
          currentFromDate={selectedFromDate}
          currentToDate={selectedToDate}
          currentRange={range || "week"}
          defaultRangeType="week"
        />
        <MetroFilterWrapper metros={metros} onApply={onMetroFilterChange} />
      </div>

      <div
        style={{ display: "flex", gap: "20px", justifyContent: "space-evenly" }}
      >
        {isLoadingChartStats || !metrosLoaded
          ? renderLoading("Loading documents stats...")
          : hasChartData && (
              <div className="flex flex-column w-full gp">
                <div className={`summary-container row-layout`}>
                  {renderDonutChart()}
                  {range !== "total" && renderProcessingTrend()}
                </div>
                <div className={`summary-container row-layout`}>
                  {range !== "total" && renderApiCallTrend()}
                </div>
              </div>
            )}
      </div>
    </div>
  );
};

export default FeedbackDataAndGraphs;
